import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HOME_WORKING_DATA, WORKING_DATA, WorkingTabs } from './screens.data';

@Component({
  selector: 'app-screens',
  imports: [CommonModule],
  templateUrl: './screens.html',
  styleUrl: './screens.css',
})
export class Screens implements OnInit {
  categories = HOME_WORKING_DATA;
  headerData = WORKING_DATA;

  selectedCategory = this.categories[0];
  currentImageIndex = 0;
  currentGlobalIndex = 0;
  isModalOpen = false;
  selectedTab = 'pos';

  ngOnInit() {
    this.selectedCategory = this.categories[0];
    this.currentImageIndex = 0;
    this.currentGlobalIndex = 0;
  }

  get allSlides() {
    return this.categories.flatMap((category) =>
      category.screenshots.map((screenshot, imageIndex) => ({
        category,
        imageIndex,
        src: this.getScreenshotSrc(screenshot),
      })),
    );
  }

  get allImages() {
    return this.allSlides.map((slide) => slide.src);
  }

  get currentCategoryScreenshots() {
    return this.selectedCategory?.screenshots || [];
  }

  get currentImages() {
    return this.currentCategoryScreenshots.map((screenshot) => this.getScreenshotSrc(screenshot));
  }

  get currentFits() {
    return this.currentCategoryScreenshots.map((screenshot) => screenshot.fit || 'contain');
  }

  private getScreenshotSrc(screenshot: { image: string }): string {
    if (screenshot.image.startsWith('/images/')) {
      return screenshot.image;
    }

    const extension = screenshot.image.includes('.webp') ? '' : '.png';
    return `${screenshot.image}${extension}`;
  }

  private syncGlobalIndexToCurrentSelection() {
    const index = this.allSlides.findIndex(
      (slide) => slide.category === this.selectedCategory && slide.imageIndex === this.currentImageIndex,
    );

    if (index !== -1) {
      this.currentGlobalIndex = index;
    }
  }

  nextImage() {
    if (!this.currentImages.length) return;

    this.currentImageIndex = (this.currentImageIndex + 1) % this.currentImages.length;
    this.syncGlobalIndexToCurrentSelection();
  }

  previousImage() {
    if (!this.currentImages.length) return;

    this.currentImageIndex =
      (this.currentImageIndex - 1 + this.currentImages.length) % this.currentImages.length;
    this.syncGlobalIndexToCurrentSelection();
  }

  selectImage(index: number) {
    if (index < 0 || index >= this.currentImages.length) return;

    this.currentImageIndex = index;
    this.syncGlobalIndexToCurrentSelection();
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  get modalImageSrc() {
    return this.currentImages[this.currentImageIndex];
  }

  isActive(index: number): boolean {
    return this.currentImageIndex === index;
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
    this.currentImageIndex = 0;
    this.currentGlobalIndex = 0;

    const categoryId = this.getTabCategoryId(tab);
    this.selectedCategory = this.categories.find((cat) => cat.id === categoryId) || this.categories[0];
  }

  getTabCategoryId(tabKey: string): string {
    const tabs = this.headerData.working.tabs as WorkingTabs;
    return tabs[tabKey]?.categoryId || '';
  }

  getTabTitle(): string {
    return this.headerData.working.workflowTitle;
  }

  getWorkflowStepAltText(): string {
    return this.headerData.working.altText.workflowStep;
  }

  getAriaLabel(labelKey: string): string {
    const ariaLabels = this.headerData.working.ariaLabels as Record<string, string>;
    return ariaLabels[labelKey] || '';
  }

  getAltText(altKey: string): string {
    const altText = this.headerData.working.altText as Record<string, string>;
    return altText[altKey] || '';
  }

  getTabData(): Array<{ key: string; name: string; icon: string }> {
    const tabs = this.headerData.working.tabs as WorkingTabs;
    return Object.entries(tabs).map(([key, tab]) => ({
      key,
      name: tab.name,
      icon: tab.icon,
    }));
  }
}
